// Nomenclature Service - Fetches countries and handicaps from API
// Falls back to local data if API is unavailable

import { countries as localCountries } from '@/data/countries';
import { apiGet, apiPostMultipart, API_BASE } from '@/utils/api';

export interface NomenclatureCountry {
  id: string;
  code: string;
  nameFr: string;
  nameAr?: string;
  nameEn?: string;
  nationality?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface NomenclatureHandicap {
  id: string;
  code: string;
  labelFr: string;
  labelAr?: string;
  description?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface NomenclatureSerieBac {
  id: string;
  code: string;
  labelFr: string;
  labelAr?: string;
  type?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface NomenclatureProvince {
  id: string;
  nameFr: string;
  isActive: boolean;
  sortOrder: number;
}

export interface NomenclatureGrade {
  id: string;
  labelFr: string;
  isActive: boolean;
  sortOrder: number;
}

export interface NomenclatureDiplome {
  id: string;
  labelFr: string;
  isActive: boolean;
  sortOrder: number;
}

export interface NomenclatureDepartement {
  id: string;
  tenantId: string;
  labelFr: string;
  code?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface DepartementUploadPreview {
  newCount: number;
  existingCount: number;
  toArchive: number;
  toReactivate: number;
  newDepartements: string[];
  existingDepartements: string[];
  toArchiveDepartements: string[];
  toReactivateDepartements: string[];
}

export interface UploadHistory {
  id: string;
  nomenclatureType: string;
  fileName: string;
  recordCount: number;
  uploadedBy: string;
  notes?: string;
  createdAt: string;
}

// Convert local countries to API format (used as fallback)
function getLocalCountries(): NomenclatureCountry[] {
  return localCountries.map((c, index) => ({
    id: c.code,
    code: c.code,
    nameFr: c.nameFr,
    nameAr: c.nameAr,
    nameEn: c.nameEn,
    nationality: c.nationality,
    isActive: true,
    sortOrder: index,
  }));
}

// Cache for countries and handicaps - initialized with local data
let countriesCache: NomenclatureCountry[] | null = getLocalCountries();
let handicapsCache: NomenclatureHandicap[] | null = null;
let seriesBacCache: NomenclatureSerieBac[] | null = null;
let provincesCache: NomenclatureProvince[] | null = null;
let gradesCache: NomenclatureGrade[] | null = null;
let diplomesCache: NomenclatureDiplome[] | null = null;
let departementsCache: NomenclatureDepartement[] | null = null;

// Fetch countries from API
export async function fetchCountries(forceRefresh = false): Promise<NomenclatureCountry[]> {
  // Return cached data immediately if available and not forcing refresh
  if (countriesCache && countriesCache.length > 0 && !forceRefresh) {
    return countriesCache;
  }

  try {
    // Public endpoint - use raw fetch without auth headers
    const response = await fetch(`${API_BASE}/nomenclature/countries`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    // Only update cache if we got valid data
    if (Array.isArray(data) && data.length > 0) {
      countriesCache = data;
      return data;
    } else {
      // API returned empty, use local data
      const localData = getLocalCountries();
      countriesCache = localData;
      return localData;
    }
  } catch (error) {
    console.warn('Failed to fetch countries from API, using local data:', error);
    // Use local countries as fallback
    const localData = getLocalCountries();
    countriesCache = localData;
    return localData;
  }
}

// Fetch handicaps from API
export async function fetchHandicaps(forceRefresh = false): Promise<NomenclatureHandicap[]> {
  if (handicapsCache && !forceRefresh) {
    return handicapsCache;
  }

  try {
    // Public endpoint - use raw fetch without auth headers
    const response = await fetch(`${API_BASE}/nomenclature/handicaps`);
    if (!response.ok) throw new Error('API error');

    const data = await response.json();
    handicapsCache = data;
    return data;
  } catch (error) {
    console.warn('Failed to fetch handicaps from API, using local data');
    // Return default handicaps
    return [
      { id: 'N', code: 'N', labelFr: 'Aucun', description: 'Pas de handicap', isActive: true, sortOrder: 0 },
      { id: 'A', code: 'A', labelFr: 'Auditif', description: 'Handicap auditif', isActive: true, sortOrder: 1 },
      { id: 'M', code: 'M', labelFr: 'Moteur', description: 'Handicap moteur', isActive: true, sortOrder: 2 },
      { id: 'V', code: 'V', labelFr: 'Visuel', description: 'Handicap visuel', isActive: true, sortOrder: 3 },
      { id: 'AM', code: 'AM', labelFr: 'Auditif + Moteur', description: 'Handicap auditif et moteur', isActive: true, sortOrder: 4 },
      { id: 'AV', code: 'AV', labelFr: 'Auditif + Visuel', description: 'Handicap auditif et visuel', isActive: true, sortOrder: 5 },
      { id: 'MV', code: 'MV', labelFr: 'Moteur + Visuel', description: 'Handicap moteur et visuel', isActive: true, sortOrder: 6 },
      { id: 'T', code: 'T', labelFr: 'Total', description: 'Handicap total', isActive: true, sortOrder: 7 },
      { id: 'XX', code: 'XX', labelFr: 'Autre', description: 'Autre type de handicap', isActive: true, sortOrder: 8 },
    ];
  }
}

// Upload countries Excel file (Super Admin only)
export async function uploadCountriesExcel(file: File, _token: string): Promise<{ success: boolean; message: string; count?: number }> {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiPostMultipart<{ success: boolean; message: string; count?: number }>(
    '/nomenclature/countries/upload',
    formData,
  );

  // Clear cache to force refresh
  countriesCache = null;

  return result;
}

// Fetch Série Bac from API
export async function fetchSeriesBac(forceRefresh = false): Promise<NomenclatureSerieBac[]> {
  if (seriesBacCache && !forceRefresh) {
    return seriesBacCache;
  }

  try {
    // Public endpoint - use raw fetch without auth headers
    const response = await fetch(`${API_BASE}/nomenclature/series-bac`);
    if (!response.ok) throw new Error('API error');

    const data = await response.json();
    seriesBacCache = data;
    return data;
  } catch (error) {
    console.warn('Failed to fetch series bac from API');
    return [];
  }
}

// Upload Série Bac Excel file
export async function uploadSeriesBacExcel(file: File, _token: string): Promise<{ success: boolean; message: string; count?: number }> {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiPostMultipart<{ success: boolean; message: string; count?: number }>(
    '/nomenclature/series-bac/upload',
    formData,
  );

  seriesBacCache = null;
  return result;
}

// Fetch Provinces from API
export async function fetchProvinces(forceRefresh = false): Promise<NomenclatureProvince[]> {
  if (provincesCache && !forceRefresh) {
    return provincesCache;
  }

  try {
    // Public endpoint - use raw fetch without auth headers
    const response = await fetch(`${API_BASE}/nomenclature/provinces`);
    if (!response.ok) throw new Error('API error');

    const data = await response.json();
    provincesCache = data;
    return data;
  } catch (error) {
    console.warn('Failed to fetch provinces from API');
    return [];
  }
}

// Upload Provinces Excel file
export async function uploadProvincesExcel(file: File, _token: string): Promise<{ success: boolean; message: string; count?: number }> {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiPostMultipart<{ success: boolean; message: string; count?: number }>(
    '/nomenclature/provinces/upload',
    formData,
  );

  provincesCache = null;
  return result;
}

// Fetch Grades from API
export async function fetchGrades(forceRefresh = false): Promise<NomenclatureGrade[]> {
  if (gradesCache && !forceRefresh) {
    return gradesCache;
  }

  try {
    // Public endpoint - use raw fetch without auth headers
    const response = await fetch(`${API_BASE}/nomenclature/grades`);
    if (!response.ok) throw new Error('API error');

    const data = await response.json();
    gradesCache = data;
    return data;
  } catch (error) {
    console.warn('Failed to fetch grades from API');
    return [];
  }
}

// Upload Grades Excel file
export async function uploadGradesExcel(file: File, _token: string): Promise<{ success: boolean; message: string; count?: number }> {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiPostMultipart<{ success: boolean; message: string; count?: number }>(
    '/nomenclature/grades/upload',
    formData,
  );

  gradesCache = null;
  return result;
}

// Fetch Diplômes from API
export async function fetchDiplomes(forceRefresh = false): Promise<NomenclatureDiplome[]> {
  if (diplomesCache && !forceRefresh) {
    return diplomesCache;
  }

  try {
    // Public endpoint - use raw fetch without auth headers
    const response = await fetch(`${API_BASE}/nomenclature/diplomes`);
    if (!response.ok) throw new Error('API error');

    const data = await response.json();
    diplomesCache = data;
    return data;
  } catch (error) {
    console.warn('Failed to fetch diplomes from API');
    return [];
  }
}

// Upload Diplômes Excel file
export async function uploadDiplomesExcel(file: File, _token: string): Promise<{ success: boolean; message: string; count?: number }> {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiPostMultipart<{ success: boolean; message: string; count?: number }>(
    '/nomenclature/diplomes/upload',
    formData,
  );

  diplomesCache = null;
  return result;
}

// Fetch Départements from API (PUBLIC endpoint - tenant-specific via x-tenant-id header)
// Can be called with either token (for authenticated users) or tenantId (for public access)
export async function fetchDepartements(tokenOrTenantId: string, forceRefresh = false, isTenantId = false): Promise<NomenclatureDepartement[]> {
  if (departementsCache && !forceRefresh) {
    return departementsCache;
  }

  try {
    // Determine headers based on whether we have a token or tenant ID
    const headers: Record<string, string> = {
      'Accept': 'application/json',
    };

    if (isTenantId) {
      // Public access with tenant ID header
      headers['X-Tenant-ID'] = tokenOrTenantId;
    } else {
      // Authenticated access with Bearer token
      headers['Authorization'] = `Bearer ${tokenOrTenantId}`;
    }

    const response = await fetch(`${API_BASE}/nomenclature/departements`, {
      headers,
    });
    if (!response.ok) throw new Error('API error');

    const data = await response.json();
    departementsCache = data;
    return data;
  } catch (error) {
    console.warn('Failed to fetch departements from API:', error);
    return [];
  }
}

// Preview Départements Excel file (analyze without importing)
export async function previewDepartementsExcel(file: File, _token: string): Promise<DepartementUploadPreview> {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiPostMultipart<DepartementUploadPreview>(
    '/nomenclature/departements/preview',
    formData,
  );

  return result;
}

// Upload Départements Excel file
export async function uploadDepartementsExcel(file: File, _token: string): Promise<{ success: boolean; message: string; count?: number }> {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiPostMultipart<{ success: boolean; message: string; count?: number }>(
    '/nomenclature/departements/upload',
    formData,
  );

  departementsCache = null;
  return result;
}

// Toggle département status (archive/unarchive)
export async function toggleDepartementStatus(departementId: string, _token: string): Promise<NomenclatureDepartement> {
  const response = await fetch(`${API_BASE}/nomenclature/departements/${departementId}/toggle-status`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur inconnue' }));
    throw new Error(error.message || 'Erreur lors du changement de statut');
  }

  departementsCache = null;
  return response.json();
}

// Get upload history (Super Admin only)
export async function fetchUploadHistory(type?: string, _token?: string): Promise<UploadHistory[]> {
  const endpoint = type
    ? `/nomenclature/uploads?type=${type}`
    : '/nomenclature/uploads';

  return apiGet<UploadHistory[]>(endpoint);
}

// Clear caches (call after upload)
export function clearNomenclatureCache() {
  countriesCache = null;
  handicapsCache = null;
  seriesBacCache = null;
  provincesCache = null;
  gradesCache = null;
  diplomesCache = null;
  departementsCache = null;
}

// Get countries synchronously (returns local data immediately)
// Useful for initial render before async fetch completes
export function getCountriesSync(): NomenclatureCountry[] {
  return countriesCache || getLocalCountries();
}
